import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import { Star, Users, Video, Clock } from 'lucide-react';

const stats = [
  { icon: Star, value: 100, suffix: '+', label: 'Five-star reviews' },
  { icon: Users, value: 12, suffix: 'K+', label: 'Solo creators' },
  { icon: Video, value: 25, suffix: 'K+', label: 'Videos generated' },
  { icon: Clock, value: 2, prefix: '<', suffix: 'min', label: 'Avg. time per video' },
];

const CountUp = ({ end, prefix = '', suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    
    let startTime = null;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

const StatsSection = () => {
  return (
    <section className="py-20 px-6" data-testid="stats-section">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-2xl bg-surface-elevated border border-white/10 flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-brand-primary" />
                </div>
              </div>
              <div className="text-4xl md:text-5xl font-heading font-black text-white mb-2">
                <CountUp 
                  end={stat.value} 
                  prefix={stat.prefix || ''} 
                  suffix={stat.suffix || ''} 
                />
              </div>
              <div className="text-gray-400 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
